// @flow
import React, { type Node } from 'react';
import { get, kebabCase } from 'lodash';
import classNames from 'classnames';
import { type FieldProps } from 'formik';

import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { Icon, type IconName } from 'Components/frameworks/icon/Icon';
import { getValidationStatus } from 'Components/frameworks/formik/inputsUtils';

type Props = {
  className?: string,
  dataTestId?: string,
  iconName: IconName,
  isDisabled: boolean,
  isRequired: boolean,
  label?: string | Node,
  ariaDescribedBy?: string,
  placeholderComponent?: (isChecked: boolean) => Node,
  ...FieldProps,
};

export const Checkbox = ({
  ariaDescribedBy,
  className,
  dataTestId,
  field,
  form,
  iconName,
  isDisabled = false,
  isRequired = false,
  label,
  onChange,
  placeholderComponent,
  iconColor,
}: Props) => {
  // Checkbox value defaults to false
  const value = get(form.values, field.name, false);
  const { isValidated, isErrored } = getValidationStatus({ ...field, value }, form, isDisabled);
  const checkboxId = `checkbox-${kebabCase(field.name)}`;
  return (
    <div
      className={classNames('flex justify-between text-base items-center', className, {
        'text-emerald': isValidated && isRequired,
        'text-red formik-errored': isErrored && isRequired,
      })}
    >
      {iconName && (
        <Icon
          className="flex-shrink-0 rtl:mr-2"
          color={iconColor || 'gray-medium'}
          name={iconName}
          width="1.375rem"
        />
      )}
      <label
        className={classNames('flex items-center w-full', {
          'cursor-pointer': !isDisabled,
          'cursor-not-allowed': isDisabled,
          'leading-none': !placeholderComponent,
        })}
        htmlFor={checkboxId}
      >
        <input
          aria-describedby={ariaDescribedBy}
          className="a11y"
          data-testid={dataTestId}
          disabled={isDisabled}
          id={checkboxId}
          type="checkbox"
          {...field}
          checked={value}
          onChange={e => {
            field.onChange(e);

            if (onChange) {
              onChange(e);
            }
          }}
        />
        {!placeholderComponent && (
          <span
            style={{ width: '20px', height: '20px' }}
            className={classNames(
              'flex flex-shrink-0 p-1 rtl:mr-3 mt-px border outline-custom-sibling rounded-tiny',
              {
                'border-gray-light': !isDisabled,
                'bg-gray-lighter border-gray-lighter': isDisabled,
              },
            )}
          >
            {!!value && <Icon color={COLORS.BLUE} name={ICONS.CHECK} width="1em" />}
          </span>
        )}
        <span
          className={classNames({
            'text-gray-darker': !isDisabled && !isErrored,
            'text-gray-light': isDisabled,
          })}
        >
          {label && <span className="leading-tight">{label}</span>}
          {isRequired && <span className="font-bold leading-tight">*</span>}
        </span>
        {placeholderComponent && placeholderComponent(value)}
      </label>
    </div>
  );
};
